/**
 * Currency minor-unit helpers — the single definition of "how many minor units
 * (decimal places) this currency has" and the integer factor between major and
 * minor units.
 *
 * BRL/USD/EUR have 2 minor digits (factor 100 — reais ↔ centavos).
 * CLP/JPY have 0 minor digits (factor 1 — there is no centavo; the integer wire
 * value IS the peso/yen).
 *
 * Derived from the platform Intl data via resolvedOptions().maximumFractionDigits
 * so it matches the i18n n(value,'currency') renderer that already handles CLP
 * correctly. Used by InputCurrency (internal keystroke/display) and by the form
 * save boundaries that convert a major-unit float to the integer wire value.
 */

/** Decimal places (minor-unit digits) for a currency: BRL/USD→2, CLP/JPY→0. */
export function currencyMinorDigits(locale: string, currency: string): number {
  try {
    const digits = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
    return typeof digits === 'number' ? digits : 2;
  } catch {
    return 2;
  }
}

/** Integer factor between major and minor units: 10 ** minorDigits. */
export function currencyFactor(locale: string, currency: string): number {
  return 10 ** currencyMinorDigits(locale, currency);
}

/**
 * Map a BCP-47 locale to its default ISO currency (mirrors InputCurrency).
 * Shared source for the recebimento/refund currency display so the locale ->
 * currency choice lives in one place.
 */
export const LOCALE_TO_CURRENCY: Record<string, string> = {
  'pt-BR': 'BRL',
  'en-US': 'USD',
  'de-DE': 'EUR',
  'en-IN': 'INR',
  'ja-JP': 'JPY',
  'es-ES': 'EUR',
  'fr-FR': 'EUR',
  'it-IT': 'EUR',
  'en-GB': 'GBP',
  'zh-CN': 'CNY',
  'es-AR': 'ARS',
  'es-MX': 'MXN',
  'es-CL': 'CLP',
  'es-EC': 'USD',
};

/**
 * Integer factor (major <-> minor units) for the currency implied by a locale.
 * Save boundaries that only know the user's locale use this to convert the
 * InputCurrency major-unit float to the integer wire value (CLP -> *1, BRL -> *100).
 */
export function currencyMinorUnitsFactor(locale: string | null | undefined): number {
  const loc = locale || 'pt-BR';
  const currency = LOCALE_TO_CURRENCY[loc] || 'BRL';
  return currencyFactor(loc, currency);
}
